import { mapGetters } from 'vuex'

/**
 * PD-4883 — the "Apps" column on the My Account child listings.
 *
 * Both listings already receive `whitelabel_ids` per row (PD-4863), so this only
 * has to turn ids into names and decide whether the column is worth showing.
 * Shared rather than duplicated so the gating rule has one definition: the two
 * screens drifting apart on a shared column is exactly what caused PD-4880.
 */
export default {
  computed: {
    // Deliberately not named `apps_list`: AccountClients already maps its own
    // getters, and a clashing key would be silently overridden by the component.
    ...mapGetters({
      whitelabelAppsList: 'user/apps_list',
    }),

    /**
     * Only worth a column when the account holds more than one app. With a single
     * app every child holds that same one, so the column would be a constant —
     * the same gate the whitelabel picker and the campaigns App column use.
     */
    showAppsColumn() {
      return (this.whitelabelAppsList || []).length > 1
    },
  },

  methods: {
    /**
     * Assigned app ids -> names, in the account's own app order rather than the
     * order the ids arrived in, so the column reads consistently down the page.
     *
     * Ids the account itself no longer holds are dropped instead of rendered as
     * raw numbers: they can only come from a whitelabel that was removed from the
     * account after being assigned to the child, and a bare id tells the reader
     * nothing.
     *
     * @param {Array<number|string>} ids
     *
     * @return {Array<string>}
     */
    whitelabelAppNames(ids) {
      const wanted = new Set((ids || []).map((id) => Number(id)))

      return (this.whitelabelAppsList || []).filter((app) => wanted.has(Number(app.id))).map((app) => app.name)
    },
  },
}
